import jwtDecode from 'jwt-decode';
import { v4 as uuid } from 'uuid';
import { useAppUserStore } from '@data/storage';
import { IToken } from '@data/utils/types';
import { AnonIdStorage } from './types';

const SESSION_IDLE_MS = 30 * 60 * 1000; // 30 min idle
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Storage-less by design: the anon id lives only in memory for the page's lifetime.
// No device read/write → no ePrivacy Art. 5(3) (cookie-consent) trigger, and the id is
// non-persistent. It resets on reload; that's intentional, not a bug.
let memoryAnon: { id: string; storage: AnonIdStorage } | null = null;
let sessionId: string | null = null;
let sessionTouchedAt = 0;

export const isValidAnonId = (value: string): boolean => UUID_RE.test(value);

export const getAnonId = (): { id: string; storage: AnonIdStorage } => {
  if (!memoryAnon) memoryAnon = { id: uuid(), storage: 'memory' };
  return memoryAnon;
};

export const adoptAnonId = (incoming: string): boolean => {
  if (!isValidAnonId(incoming)) return false;
  memoryAnon = { id: incoming, storage: 'memory' };
  return true;
};

export const getSessionId = (): string => {
  const now = Date.now();
  if (!sessionId || now - sessionTouchedAt > SESSION_IDLE_MS) {
    sessionId = uuid();
  }
  sessionTouchedAt = now;
  return sessionId;
};

export const getUserId = (): string | undefined => {
  const token = useAppUserStore.getState().user?.token;
  if (!token) return undefined;
  try {
    return jwtDecode<IToken>(token).user_id;
  } catch {
    return undefined;
  }
};

export const resetPulseIdentityForTests = (): void => {
  memoryAnon = null;
  sessionId = null;
  sessionTouchedAt = 0;
};
